import { z } from 'zod';
import { isEmailDomainAllowed } from './admin';
import { ADMIN_PERMISSIONS } from './permissions';
import { roleIdSchema } from './roles';

/**
 * Automatic sign-up (owner request; deviates from brief §7.1/§7.2): a person who passes a
 * qualified sign-in for this school gets an account on first sign-in with the default role.
 * Off by default. Invites still exist for special roles.
 */
export const DEFAULT_AUTO_ENROLL_ROLE_ID = 'user';

export const autoEnrollSettingsSchema = z.object({
  enabled: z.boolean(),
  defaultRoleId: roleIdSchema,
  methods: z.object({ google: z.boolean(), microsoft: z.boolean(), emailOtp: z.boolean() }),
});
export type AutoEnrollSettings = z.infer<typeof autoEnrollSettingsSchema>;

export const AUTO_ENROLL_OFF: AutoEnrollSettings = {
  enabled: false,
  defaultRoleId: DEFAULT_AUTO_ENROLL_ROLE_ID,
  methods: { google: false, microsoft: false, emailOtp: false },
};

export type AutoEnrollMethod = keyof AutoEnrollSettings['methods'];

/** What the school has configured that makes a sign-in "qualified". */
export interface QualificationConfig {
  googleHostedDomain: string | null;
  microsoftEntraTenantId: string | null;
  emailOtpAllowedDomains: readonly string[];
}

export type AutoEnrollConfigIssue =
  | 'ROLE_HAS_ADMIN_PERMISSIONS'
  | 'GOOGLE_NEEDS_HOSTED_DOMAIN'
  | 'MICROSOFT_NEEDS_ENTRA_TENANT'
  | 'EMAIL_CODE_NEEDS_ALLOWED_DOMAINS';

/** Validates a settings change; the admin UI shows each issue next to its control. */
export function autoEnrollConfigIssues(
  settings: AutoEnrollSettings,
  defaultRole: { permissions: readonly string[] },
  config: QualificationConfig,
): AutoEnrollConfigIssue[] {
  if (!settings.enabled) return [];
  const adminIds = new Set(Object.keys(ADMIN_PERMISSIONS));
  const issues: AutoEnrollConfigIssue[] = [];
  if (defaultRole.permissions.some((p) => adminIds.has(p)))
    issues.push('ROLE_HAS_ADMIN_PERMISSIONS');
  if (settings.methods.google && !config.googleHostedDomain)
    issues.push('GOOGLE_NEEDS_HOSTED_DOMAIN');
  if (settings.methods.microsoft && !config.microsoftEntraTenantId)
    issues.push('MICROSOFT_NEEDS_ENTRA_TENANT');
  if (settings.methods.emailOtp && config.emailOtpAllowedDomains.length === 0) {
    issues.push('EMAIL_CODE_NEEDS_ALLOWED_DOMAINS');
  }
  return issues;
}

/** Verified facts about a sign-in that has no matching account yet. */
export interface SignInFacts {
  method: AutoEnrollMethod;
  email: string;
  emailVerified: boolean;
  /** Google `hd` claim. */
  googleHostedDomain?: string | null;
  /** Microsoft `tid` claim. */
  microsoftTenantId?: string | null;
  /** Entra B2B guest in the school's tenant (`acct` = 1, foreign `idp`, or a #EXT# UPN). */
  microsoftGuest?: boolean;
}

export type AutoEnrollDecision =
  | { create: true; roleId: string }
  | { create: false; reason: 'DISABLED' | 'METHOD_OFF' | 'EMAIL_NOT_VERIFIED' | 'NOT_QUALIFIED' };

/** Decides whether to create an account on first sign-in. Pure; Keycloak and tenant-api share it. */
export function autoEnrollDecision(
  settings: AutoEnrollSettings,
  config: QualificationConfig,
  facts: SignInFacts,
): AutoEnrollDecision {
  if (!settings.enabled) return { create: false, reason: 'DISABLED' };
  if (!settings.methods[facts.method]) return { create: false, reason: 'METHOD_OFF' };
  if (!facts.emailVerified) return { create: false, reason: 'EMAIL_NOT_VERIFIED' };
  const qualified = qualifies(config, facts);
  return qualified
    ? { create: true, roleId: settings.defaultRoleId }
    : { create: false, reason: 'NOT_QUALIFIED' };
}

function qualifies(config: QualificationConfig, facts: SignInFacts): boolean {
  switch (facts.method) {
    case 'google':
      return (
        !!config.googleHostedDomain &&
        facts.googleHostedDomain?.toLowerCase() === config.googleHostedDomain.toLowerCase() &&
        isEmailDomainAllowed(facts.email, [config.googleHostedDomain.toLowerCase()])
      );
    case 'microsoft':
      return (
        !!config.microsoftEntraTenantId &&
        facts.microsoftTenantId === config.microsoftEntraTenantId &&
        facts.microsoftGuest !== true
      );
    case 'emailOtp':
      return (
        config.emailOtpAllowedDomains.length > 0 &&
        isEmailDomainAllowed(facts.email, config.emailOtpAllowedDomains)
      );
  }
}

/** How a user account came to exist; shown as a label in Admin → Users. */
export const userSourceSchema = z.enum(['invited', 'auto', 'seeded']);
export type UserSource = z.infer<typeof userSourceSchema>;
